/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  Autowired,
  Component,
  Constants,
  GridOptionsWrapper,
  ICellRendererComp,
  ICellRendererParams,
  RefSelector,
  RowNode,
  Utils,
} from 'ag-grid-community';

export interface GroupCellRendererOptions {
  hasChildrenProperty: string;
  isOpenedProperty: string;
  onOpened: ProcedureFn<{ node: RowNode; open: boolean }>;
}

export interface GroupCellRendererParams extends ICellRendererParams, GroupCellRendererOptions {
}

export class GroupCellRenderer extends Component implements ICellRendererComp {

  private static TEMPLATE =
    '<span class="grid-group">' +
    '<span class="grid-group-expanded" ref="eExpanded"></span>' +
    '<span class="grid-group-contracted" ref="eContracted"></span>' +
    '<span class="grid-group-value" ref="eValue"></span>' +
    '</span>';

  @Autowired('gridOptionsWrapper') private gridOptionsWrapper: GridOptionsWrapper;

  @RefSelector('eExpanded') private eExpanded: HTMLElement;
  @RefSelector('eContracted') private eContracted: HTMLElement;
  @RefSelector('eValue') private eValue: HTMLElement;

  private params: GroupCellRendererParams;
  private indentClass: string;

  constructor() {
    super(GroupCellRenderer.TEMPLATE);
  }

  init(params: GroupCellRendererParams): void {
    this.params = params;

    this.addExpandAndContract();
    this.addValueElement();
    this.setupIndent();
  }

  refresh(): boolean {
    this.showExpandAndContractIcons();
    return false;
  }

  private setupIndent(): void {
    this.addDestroyableEventListener(this.params.node, RowNode.EVENT_UI_LEVEL_CHANGED, this.setIndent.bind(this));
    this.setIndent();
  }

  private setIndent(): void {
    const paddingCount = this.params.node.uiLevel;
    const cellEl = this.getGui();

    if (this.indentClass) {
      cellEl.classList.remove(this.indentClass);
    }

    this.indentClass = 'grid-group-indent-' + paddingCount;
    cellEl.classList.add(this.indentClass);
  }

  private addExpandAndContract(): void {
    const params = this.params;
    const eGroupCell = params.eGridCell;
    const eExpandedIcon = Utils.createIconNoSpan('groupExpanded', this.gridOptionsWrapper, <any>void 0);
    const eContractedIcon = Utils.createIconNoSpan('groupContracted', this.gridOptionsWrapper, <any>void 0);
    this.eExpanded.appendChild(eExpandedIcon);
    this.eContracted.appendChild(eContractedIcon);

    this.addDestroyableEventListener(this.eExpanded, 'click', this.onExpandClicked.bind(this));
    this.addDestroyableEventListener(this.eContracted, 'click', this.onExpandClicked.bind(this));

    // expand / contract as the user hits enter
    this.addDestroyableEventListener(eGroupCell, 'keydown', this.onKeyDown.bind(this));
    this.addDestroyableEventListener(
      params.node,
      RowNode.EVENT_DATA_CHANGED,
      this.showExpandAndContractIcons.bind(this));
    this.showExpandAndContractIcons();
  }

  private addValueElement(): void {
    this.createLeafCell();
  }

  private createLeafCell(): void {
    // tslint:disable-next-line:prefer-conditional-expression
    if (Utils.exists(this.params.value)) {
      this.eValue.innerHTML = this.params.valueFormatted ? this.params.valueFormatted : this.params.value;
    } else {
      this.eValue.innerHTML = '&nbsp;';
    }
  }

  private onKeyDown(event: KeyboardEvent): void {
    if (Utils.isKeyPressed(event, Constants.KEY_ENTER)) {
      const cellEditable = this.params.column.isCellEditable(this.params.node);
      if (cellEditable) {
        return;
      }
      event.preventDefault();
      this.onExpandOrContract();
    }
  }

  private onExpandClicked(mouseEvent: MouseEvent): void {
    if (Utils.isStopPropagationForAgGrid(mouseEvent)) {
      return;
    }
    this.onExpandOrContract();
  }

  private onExpandOrContract(): void {
    const { node, hasChildrenProperty, isOpenedProperty, onOpened } = this.params;

    if (node.data[hasChildrenProperty]) {
      onOpened({ node, open: !node.data[isOpenedProperty] });
    }
  }

  private isExpandable(): boolean {
    const { node, hasChildrenProperty } = this.params;
    return node.data[hasChildrenProperty];
  }

  private showExpandAndContractIcons(): void {
    const { node, isOpenedProperty } = this.params;

    if (this.isExpandable()) {
      // if expandable, show one based on expand state.
      const expanded = node.data[isOpenedProperty];
      Utils.setVisible(this.eContracted, !expanded);
      Utils.setVisible(this.eExpanded, expanded);
    } else {
      // it not expandable, show neither
      Utils.setVisible(this.eExpanded, false);
      Utils.setVisible(this.eContracted, false);
    }
  }
}
